import { useFlags } from 'launchdarkly-react-client-sdk';

import { useIsFullstackDispensary } from 'src/hooks/use-is-fullstack-dispensary';
import { useSpecialsPageSettings } from './use-specials-page-settings';

/**
 * Single source of truth for whether the redesigned specials page (and everything that only
 * exists inside it - category tiles, special duration, menu filter chips, the specials filter
 * sidebar entry, etc.) should render for the current dispensary.
 *
 * Gated on all three of:
 *   1. LD rollout flag `ecomm.specials-redesign.rollout`
 *   2. Per-dispensary `showSpecialsRedesign` admin toggle
 *      (`storeSettings.specialsPage.showSpecialsRedesign`, see `useSpecialsPageSettings`)
 *   3. Dispensary uses Dutchie POS (Leaflogix) as source of truth — the redesign fetches from
 *      `/rest/specials/menu_cards` (PAPI), which only has data for full-stack dispensaries.
 *      Without this gate, non-DPOS dispensaries with the flag+toggle on would render a completely
 *      blank specials page (every downstream component early-returns null on empty PAPI data).
 */
export function useSpecialsRedesignEnabled(): boolean {
  const flags = useFlags();
  const specialsRedesignFlagEnabled = flags['ecomm.specials-redesign.rollout'] ?? false;
  const { showSpecialsRedesign } = useSpecialsPageSettings();
  const isFullstackDispensary = useIsFullstackDispensary();

  return Boolean(specialsRedesignFlagEnabled) && Boolean(showSpecialsRedesign) && isFullstackDispensary;
}
